import { useEffect } from "react";
import { handlePremiumsExhibitToggle } from "./handlePremiumsExhibitToggle";
import DimensionalDifferenceTwoColumnContainer from "components/ddiff-containers/ddiff-two-column-container/DimensionalDifferenceTwoColumnContainer";
import AnimatedExhibitInfographicHeadline from "./AnimatedExhibitInfoHeadline";
import { getDecodedHtml } from "utils/getDecodedHtml";
import { DToolTipAdapterContainer } from "components/ui-platform";

type PremiumsCards = {
	id: string;
	chartBody: string;
	media: {
		type: string;
		imageUrl: string;
		imageAlt: string;
	};
};

type PremiumsTabs = {
	id: string;
	titleTab: string;
	littleIPopupText: string;
	disclosureText: string;
	cards: PremiumsCards[];
};

type AnimatedPremiumsExhibitProps = {
	data: {
		infographicHeadline: string;
		title: string;
		tabs: PremiumsTabs[];
	};
};

const AnimatedPremiumsExhibit = ({ data }: AnimatedPremiumsExhibitProps) => {
	useEffect(() => {
		handlePremiumsExhibitToggle();
	}, []);
	const cards = data.tabs.flatMap((tab) => tab.cards);
	return (
		<DimensionalDifferenceTwoColumnContainer choiceNumber={3}>
			<AnimatedExhibitInfographicHeadline
				headline={data.infographicHeadline}
				className="ddiff-animated-exhibit-premiums-headline"
			/>
			<div className="ddiff-animated-exhibit-premiums">
				<div className="ddiff-premiums-info">
					<div className="ddiff-premiums-info-title">
						<p className="t-label-sm-heavy tc-cool-gray-01">{data.title}</p>
					</div>
					<div className="ddiff-premiums-switch-container">
						<p className="ddiff-switch-title-equity t-body-md tc-cool-gray-02 tc-poppy">
							{data.tabs[0].titleTab}
						</p>
						<div className="ddiff-premiums-switch" role="button">
							<input
								type="checkbox"
								id="ddiff-premiums-switch"
								name="ddiff-premiums-switch"
								data-a-evt="Subcontent view"
								data-a-comp="Premiums Animated Exhibit"
								data-a-text={data.tabs[0].titleTab}
								tabIndex={0}
							/>
							<span className="ddiff-slider ddiff-slider-pulse"></span>
							<label htmlFor="ddiff-premiums-switch" className="ddiff-premiums-switch-label">
								Premiums View Switch
							</label>
						</div>
						<p className="ddiff-switch-title-fixed t-body-md tc-cool-gray-02">
							{data.tabs[1].titleTab}
						</p>
					</div>
				</div>
				<div className="ddiff-premiums-charts">
					{cards.map((card, i) => {
						return (
							<div className={`ddiff-premium-chart-${i} ddiff-premium`} key={card.id}>
								<div className="ddiff-premium-copy">
									<div
										className={`${
											i > 2 ? "ddiff-fixed-copy ddiff-premiums-hide" : "ddiff-equity-copy"
										}`}
									>
										<div
											className="t-body-md"
											dangerouslySetInnerHTML={{ __html: getDecodedHtml(card.chartBody) }}
										/>
									</div>
								</div>
								<div
									className={`ddiff-premiums-image ${
										i > 2 ? "ddiff-fixed-svg" : "ddiff-equity-svg"
									}`}
								>
									<img src={card.media.imageUrl} alt={card.media.imageAlt} />
								</div>
							</div>
						);
					})}
				</div>
				{data.tabs.map((tab, i) => {
					const littleIPadding =
						i === 0 && tab.disclosureText
							? "ddiff-premiums-equity-has-disclosure"
							: i === 1 && tab.disclosureText
								? "ddiff-premiums-fixed-has-disclosure"
								: "";
					return (
						<div
							className={`ddiff-premiums-mobile ${i === 1 ? "ddiff-premiums-mobile-fixed" : ""}`}
							key={tab.id}
						>
							<div className="ddiff-premiums-chart-title-mobile">
								<p className="t-body-md-heavy tc-cool-gray-02">{tab.titleTab}</p>
							</div>
							<div className="ddiff-premiums-charts-mobile">
								{tab.cards.map((chart, chartIdx) => {
									return (
										<div
											className={`ddiff-premium-chart-${chartIdx} ddiff-premium`}
											key={`mobile-equity-${chart.id}`}
										>
											<div className="ddiff-premium-copy">
												<div
													className="t-body-md"
													dangerouslySetInnerHTML={{ __html: getDecodedHtml(chart.chartBody) }}
												/>
											</div>
											<div className="ddiff-premium-mobile-svg">
												<img src={chart.media.imageUrl} alt={chart.media.imageAlt} />
											</div>
										</div>
									);
								})}
							</div>
							{tab.disclosureText ? (
								<div
									className="ddiff-premiums-disclosure-mobile t-body-md-heavy"
									dangerouslySetInnerHTML={{ __html: getDecodedHtml(tab.disclosureText) }}
								/>
							) : null}
							{tab.littleIPopupText ? (
								<div className={`ddiff-premiums-little-i-mobile ${littleIPadding}`}>
									<DToolTipAdapterContainer align="right" content={tab.littleIPopupText} />
								</div>
							) : null}
						</div>
					);
				})}
				{data.tabs[0].disclosureText || data.tabs[1].disclosureText ? (
					<div className="ddiff-premiums-disclosure">
						{data.tabs[0].disclosureText ? (
							<div
								className="ddiff-equity-disclosure t-body-md-heavy"
								dangerouslySetInnerHTML={{ __html: getDecodedHtml(data.tabs[0].disclosureText) }}
							/>
						) : null}
						{data.tabs[1].disclosureText ? (
							<div
								className="ddiff-fixed-disclosure t-body-md-heavy ddiff-premiums-disclosure-hide"
								dangerouslySetInnerHTML={{ __html: getDecodedHtml(data.tabs[1].disclosureText) }}
							/>
						) : null}
					</div>
				) : null}
				{data.tabs[0].littleIPopupText ? (
					<div
						className={`ddiff-premiums-little-i ddiff-premiums-little-i-equity ${
							data.tabs[0].disclosureText ? "ddiff-premiums-equity-has-disclosure" : ""
						}`}
					>
						<DToolTipAdapterContainer align="right" content={data.tabs[0].littleIPopupText} />
					</div>
				) : null}
				{data.tabs[1].littleIPopupText ? (
					<div
						className={`ddiff-premiums-little-i ddiff-premiums-little-i-fixed ${
							data.tabs[1].disclosureText ? "ddiff-premiums-fixed-has-disclosure" : ""
						} ddiff-premiums-little-i-hide`}
					>
						<DToolTipAdapterContainer align="right" content={data.tabs[1].littleIPopupText} />
					</div>
				) : null}
			</div>
		</DimensionalDifferenceTwoColumnContainer>
	);
};

export default AnimatedPremiumsExhibit;
